import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Divider,
  Box,
  useMediaQuery,
  useTheme,
  IconButton,
  SelectChangeEvent,
  alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useUserSettings, UserSettings } from '../../context/UserSettingsContext';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
  currentFormValues?: any;
}

const SettingsDialog: React.FC<SettingsDialogProps> = ({ open, onClose, currentFormValues }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { settings, updateSettings, loading } = useUserSettings();
  const { appAccess } = useAuth();
  const location = useLocation();
  
  // Local state to track form values
  const [formValues, setFormValues] = useState<Partial<UserSettings>>({
    system_of_units: '',
    theme: '',
  });

  // Update local state when settings change
  useEffect(() => {
    if (settings) {
      setFormValues({
        system_of_units: settings.system_of_units,
        theme: settings.theme,
      });
    }
  }, [settings]);

  // Handle input changes
  const handleChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormValues((prev: Partial<UserSettings>) => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle save
  const handleSave = async () => {
    try {
      // If unit system changed
      if (formValues.system_of_units !== settings?.system_of_units) {
        // If we're in an app, trigger the switch button functionality
        // Use dynamic check based on user's accessible apps
        const isInApp = appAccess?.accessible_apps.some(app => 
          location.pathname === app.app_path
        ) ?? false;
        
        if (isInApp) {
          // Create and dispatch a custom event to trigger the switch button
          const event = new CustomEvent('unitSystemChange', { 
            detail: { 
              newUnitSystem: formValues.system_of_units 
            } 
          });
          window.dispatchEvent(event);
        }
        
        // Always update settings
        await updateSettings({ system_of_units: formValues.system_of_units });
      } else {
        // If only theme changed, just update settings
        await updateSettings(formValues);
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <DialogTitle 
        component="div"
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          py: 2
        }}
      >
        <Typography variant="h6" fontWeight="bold" component="div">
          Settings
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.contrastText, 0.1)
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography 
              variant="subtitle1" 
              fontWeight="bold" 
              color="primary"
              sx={{ mb: 2 }}
            >
              General Settings
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 0,
              gap: 1,
              width: '100%'
            }}>
              <Typography 
                sx={{ 
                  minWidth: '200px',
                  width: '200px',
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}
              >
                System of Units
              </Typography>
              <Box sx={{ 
                flexGrow: 1, 
                maxWidth: '500px'
              }}>
                <FormControl fullWidth>
                  <Select
                    name="system_of_units"
                    value={formValues.system_of_units ?? ''}
                    onChange={handleChange}
                    sx={{ 
                      borderRadius: 1,
                      height: '32px',
                      '& .MuiSelect-select': {
                        fontSize: '0.875rem',
                        padding: '4px 8px'
                      },
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main
                        }
                      }
                    }}
                  >
                    <MenuItem value="SI">SI (Metric)</MenuItem>
                    <MenuItem value="imperial">Imperial</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography 
              variant="subtitle1" 
              fontWeight="bold" 
              color="primary"
              sx={{ mb: 2 }}
            >
              Appearance
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 0,
              gap: 1,
              width: '100%'
            }}>
              <Typography 
                sx={{ 
                  minWidth: '200px',
                  width: '200px',
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}
              >
                Theme
              </Typography>
              <Box sx={{ 
                flexGrow: 1, 
                maxWidth: '500px'
              }}>
                <FormControl fullWidth>
                  <Select
                    name="theme"
                    value={formValues.theme ?? ''}
                    onChange={handleChange}
                    sx={{ 
                      borderRadius: 1,
                      height: '32px',
                      '& .MuiSelect-select': {
                        fontSize: '0.875rem',
                        padding: '4px 8px'
                      },
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main
                        }
                      }
                    }}
                  >
                    <MenuItem value="light">Light</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                    <MenuItem value="system">Use System Setting</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button 
          onClick={onClose} 
          color="inherit"
          sx={{ 
            px: 2,
            '&:hover': {
              bgcolor: alpha(theme.palette.text.primary, 0.05)
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          variant="contained"
          disabled={loading}
          sx={{ 
            px: 3,
            '&:hover': {
              boxShadow: theme.shadows[4]
            }
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog;
